import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Copy, ExternalLink, X, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { ALBUMS, getAlbum, coverDisplayLabel } from "@/config/albumCatalog";
import AlbumCard from "@/components/AlbumCard";

/**
 * Phase 7 — Admin: Album Completion Registry management.
 * ────────────────────────────────────────────────────
 * Fast-entry workflow: album_type + tournament_year sticky, collector fields
 * clear on save, next sequence pre-filled, live cert-number preview.
 */
export default function RegistryAdmin() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState({ q: "", album_type: "", status: "" });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);          // full row when editing
  const [tournamentYear, setTournamentYear] = useState(2026);

  const load = () => {
    setRows(null);
    const params = {};
    if (filter.q) params.q = filter.q;
    if (filter.album_type) params.album_type = filter.album_type;
    if (filter.status) params.status = filter.status;
    api.get("/admin/registry/certificates", { params })
      .then((r) => setRows(r.data.certificates || []))
      .catch((e) => { setRows([]); toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); });
  };

  useEffect(() => {
    api.get("/admin/registry/settings")
      .then((r) => setTournamentYear(r.data.tournament_year || 2026))
      .catch(() => setTournamentYear(2026));
    load();
  }, []);

  const onFilterSubmit = (e) => { e.preventDefault(); load(); };

  return (
    <div data-testid="registry-admin">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl md:text-3xl">Certificate Management</h2>
          <p className="text-sm text-[var(--smoke)] mt-1">
            Manage HC / SC certificates. Tournament year:{" "}
            <TournamentYearInline year={tournamentYear} onChange={setTournamentYear} />
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="btn-red inline-flex items-center gap-2"
          data-testid="registry-admin-new-cert"
        >
          <Plus size={16} /> New Certificate
        </button>
      </div>

      {/* Filters */}
      <form onSubmit={onFilterSubmit} className="mt-8 grid gap-3 md:grid-cols-[1fr_auto_auto_auto]" data-testid="registry-admin-filters">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
          <input
            type="text"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            placeholder="Search certificate # / name / phone / instagram…"
            className="w-full rounded-full border-2 border-[var(--ink)] pl-11 pr-4 py-2.5 text-sm"
            data-testid="registry-admin-search-input"
          />
        </div>
        <select
          value={filter.album_type}
          onChange={(e) => setFilter({ ...filter, album_type: e.target.value })}
          className="rounded-full border-2 border-[var(--ink)] px-4 py-2.5 text-sm bg-white"
          data-testid="registry-admin-filter-type"
        >
          <option value="">All types</option>
          <option value="HC">Hardcover</option>
          <option value="SC">Softcover</option>
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="rounded-full border-2 border-[var(--ink)] px-4 py-2.5 text-sm bg-white"
          data-testid="registry-admin-filter-status"
        >
          <option value="">All status</option>
          <option value="verified">Verified</option>
          <option value="replacement">Replacement</option>
          <option value="revoked">Revoked</option>
          <option value="test">Test</option>
        </select>
        <button type="submit" className="btn-red text-sm" data-testid="registry-admin-search-submit">Apply</button>
      </form>

      {/* Table */}
      <div className="mt-8 rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden" data-testid="registry-admin-table">
        <table className="w-full text-sm text-left">
          <thead className="bg-[var(--panini-yellow-soft)] text-[var(--ink)] uppercase text-xs tracking-widest">
            <tr>
              <th className="px-4 py-3 font-bold">Certificate #</th>
              <th className="px-4 py-3 font-bold">Type</th>
              <th className="px-4 py-3 font-bold">Collector</th>
              <th className="px-4 py-3 font-bold">Completion</th>
              <th className="px-4 py-3 font-bold">Published</th>
              <th className="px-4 py-3 font-bold">Claimed</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold"></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[var(--smoke)]">Loading…</td></tr>
            )}
            {rows !== null && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-14 text-center text-[var(--smoke)]" data-testid="registry-admin-empty">
                No certificates yet. Click <strong>New Certificate</strong> to add the first one.
              </td></tr>
            )}
            {rows?.map((r) => (
              <tr key={r.cert_number} className="border-t border-[var(--ink)]/10 hover:bg-[var(--panini-yellow-soft)]/30 transition-colors" data-testid={`registry-admin-row-${r.cert_number}`}>
                <td className="px-4 py-3 font-mono text-[var(--panini-red)]">{r.cert_number}</td>
                <td className="px-4 py-3">{r.album_type}</td>
                <td className="px-4 py-3">{r.collector_name}</td>
                <td className="px-4 py-3 text-[var(--smoke)]">{r.completion_date}</td>
                <td className="px-4 py-3"><Pill on={r.published}>{r.published ? "Yes" : "No"}</Pill></td>
                <td className="px-4 py-3"><Pill on={r.is_claimed} tone="gold">{r.is_claimed ? "Yes" : "No"}</Pill></td>
                <td className="px-4 py-3"><StatusPill status={r.certificate_status} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => { setEditing(r); setShowForm(true); }}
                    className="text-[var(--panini-red)] font-bold text-xs uppercase tracking-widest hover:underline"
                    data-testid={`registry-admin-edit-${r.cert_number}`}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <CertificateForm
          key={editing ? editing.cert_number : "new"}
          editing={editing}
          defaultYear={tournamentYear}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={(refreshOnly) => { load(); if (refreshOnly) { setShowForm(false); setEditing(null); } }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
function TournamentYearInline({ year, onChange }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(String(year));
  useEffect(() => setVal(String(year)), [year]);
  const save = () => {
    const y = parseInt(val, 10);
    if (isNaN(y) || y < 1900 || y > 2100) { toast.error("Year out of range"); return; }
    api.patch("/admin/registry/settings", { tournament_year: y })
      .then((r) => { onChange(r.data.tournament_year); setEdit(false); toast.success("Tournament year updated."); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Save failed"));
  };
  if (!edit) {
    return (
      <button type="button" onClick={() => setEdit(true)} className="font-bold text-[var(--ink)] underline decoration-dotted underline-offset-4" data-testid="registry-year-inline">
        {year}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" min="1900" max="2100" value={val} onChange={(e) => setVal(e.target.value)}
             className="w-24 rounded border-2 border-[var(--ink)] px-2 py-0.5 text-sm" data-testid="registry-year-input" />
      <button type="button" onClick={save} className="text-xs font-bold text-[var(--panini-red)]" data-testid="registry-year-save">Save</button>
      <button type="button" onClick={() => { setEdit(false); setVal(String(year)); }} className="text-xs text-[var(--smoke)]">Cancel</button>
    </span>
  );
}

// ────────────────────────────────────────────────────────────────
function CertificateForm({ editing, defaultYear, onClose, onSaved }) {
  const isEdit = !!editing;
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [confirming, setConfirming] = useState(null);   // { mode, body } — confirmation modal
  const [dupWarning, setDupWarning] = useState(null);   // { mode, body, matches[] } — soft duplicate warning

  const [form, setForm] = useState(() => {
    if (editing) return {
      album_slug: editing.album_slug || "",
      album_type: editing.album_type || "HC",
      tournament_year: editing.tournament_year || defaultYear,
      cert_number: editing.cert_number || "",
      collector_full_name: editing.collector_full_name || editing.collector_name || "",
      collector_nickname: editing.collector_nickname || "",
      public_display_name: editing.public_display_name || editing.collector_name || "",
      completion_date: editing.completion_date || "",
      phone_number: editing.phone_number || "",
      instagram_handle: editing.instagram_handle || "",
      admin_notes: editing.admin_notes || "",
      social_profile_permission: !!editing.social_profile_permission,
      published: !!editing.published,
      certificate_status: editing.certificate_status || "verified",
    };
    // First-time entry defaults to the newest tournament (CAN/MEX/USA 2026)
    const firstAlbum = ALBUMS[0];
    return {
      album_slug: firstAlbum.slug,
      album_type: "HC",
      tournament_year: firstAlbum.year,
      cert_number: "",
      collector_full_name: "",
      collector_nickname: "",
      public_display_name: "",
      completion_date: "",
      phone_number: "",
      instagram_handle: "",
      admin_notes: "",
      social_profile_permission: false,
      published: false,
      certificate_status: "verified",
    };
  });

  // When album_slug changes, mirror tournament_year automatically.
  const selectedAlbum = getAlbum(form.album_slug);
  useEffect(() => {
    if (isEdit) return;
    if (selectedAlbum && form.tournament_year !== selectedAlbum.year) {
      setForm((f) => ({ ...f, tournament_year: selectedAlbum.year }));
    }
  }, [form.album_slug, isEdit, selectedAlbum, form.tournament_year]);

  // Fetch next-sequence hint whenever cover type / year changes — used PURELY
  // as an informational "Suggested next number" hint. Never overwrites the
  // admin's manually typed value.
  const [seqHint, setSeqHint] = useState(null);
  useEffect(() => {
    if (isEdit) return;
    api.get("/admin/registry/next-sequence", {
      params: { album_type: form.album_type, year: form.tournament_year },
    })
      .then((r) => setSeqHint(r.data))
      .catch(() => setSeqHint(null));
  }, [form.album_type, form.tournament_year, isEdit]);

  // Live validation of the manually-entered cert_number.
  const certNumberError = useMemo(() => {
    if (isEdit) return null;                                          // immutable on edit
    const raw = (form.cert_number || "").trim();
    if (!raw) return null;                                            // empty = no error yet; save button gate handles it
    if (/\s/.test(raw)) return "Certificate number must not contain spaces.";
    const m = /^(HC|SC)-(\d{4})-(\d{4})$/.exec(raw);
    if (!m) return "Format must be HC-YYYY-#### or SC-YYYY-#### (4-digit year, 4-digit sequence).";
    const [, prefix, yearStr] = m;
    if (prefix !== form.album_type) {
      const coverLabel = form.album_type === "HC" ? "Hardcover" : "Softcover";
      return `Prefix ${prefix}- does not match the selected cover type (${coverLabel}).`;
    }
    if (parseInt(yearStr, 10) !== parseInt(form.tournament_year, 10)) {
      return `Year must match the selected album's Tournament Year: ${form.tournament_year}.`;
    }
    return null;
  }, [form.cert_number, form.album_type, form.tournament_year, isEdit]);

  // Preview shows whatever the admin has typed (once it parses cleanly), or a placeholder.
  const previewCertNumber = useMemo(() => {
    if (isEdit) return editing.cert_number;
    const raw = (form.cert_number || "").trim().toUpperCase();
    if (/^(HC|SC)-\d{4}-\d{4}$/.test(raw)) return raw;
    return "…";
  }, [form.cert_number, isEdit, editing]);

  const collectorPrimaryName =
    (form.public_display_name || form.collector_nickname || form.collector_full_name || "").trim();

  const canSubmit =
    !!form.album_slug &&
    !!form.album_type &&
    !!form.collector_full_name.trim() &&
    !!collectorPrimaryName &&
    !!form.completion_date &&
    (isEdit || (!!form.cert_number.trim() && !certNumberError));

  const buildBody = () => ({
    album_slug: form.album_slug,
    album_type: form.album_type,
    cert_number: isEdit ? undefined : form.cert_number.trim().toUpperCase(),
    collector_full_name: form.collector_full_name.trim(),
    collector_nickname: form.collector_nickname.trim(),
    public_display_name: (form.public_display_name || form.collector_full_name).trim(),
    collector_name: collectorPrimaryName, // backend-required legacy field
    completion_date: form.completion_date,
    phone_number: form.phone_number.trim(),
    instagram_handle: form.instagram_handle.trim(),
    admin_notes: form.admin_notes.trim(),
    social_profile_permission: !!form.social_profile_permission,
    published: !!form.published,
    certificate_status: form.certificate_status,
  });

  // Per-field validation errors — surfaced inline under each field so the
  // admin can never see a validation error for a field they can't see.
  const [fieldErrors, setFieldErrors] = useState({});

  const validateAll = () => {
    const errs = {};
    if (!form.album_slug) errs.album_slug = "Select an album.";
    if (!form.album_type) errs.album_type = "Select a cover type.";
    if (!isEdit) {
      if (!form.cert_number.trim()) errs.cert_number = "Enter the certificate number printed on the physical certificate.";
      else if (certNumberError) errs.cert_number = certNumberError;
    }
    if (!form.collector_full_name.trim()) errs.collector_full_name = "Enter the collector's full name.";
    if (!collectorPrimaryName) errs.public_display_name = "A public display name (or full name) is required.";
    if (!form.completion_date) errs.completion_date = "Enter the inspection (completion) date.";
    return errs;
  };

  const focusFirstError = (errs) => {
    // Order matches on-screen top-to-bottom so the first invalid field shown to
    // the admin is always the top-most one visually.
    const order = ["collector_full_name", "public_display_name", "album_slug", "album_type", "completion_date", "cert_number"];
    const firstKey = order.find((k) => errs[k]);
    if (!firstKey) return;
    const map = {
      collector_full_name: "registry-form-collector-full-name",
      public_display_name: "registry-form-public-display-name",
      album_slug: "registry-form-album-slug",
      album_type: "registry-form-album-type",
      completion_date: "registry-form-completion-date",
      cert_number: "registry-form-cert-number",
    };
    const el = document.querySelector(`[data-testid="${map[firstKey]}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => { try { el.focus(); } catch { /* focus is best-effort */ } }, 250);
    }
  };

  const requestSubmit = (mode) => {
    const errs = validateAll();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      focusFirstError(errs);
      return;
    }
    setFieldErrors({});
    if (isEdit) {
      // No confirmation modal on edit — direct save. Cert number never changes.
      performSubmit(mode, buildBody());
      return;
    }
    // Phase 4A · Duplicate warning pre-submit.
    // Ask the backend if a certificate with the same collector name +
    // album + album_type + completion_date already exists. When it
    // does, open the DupWarning modal for explicit Create Anyway
    // confirmation. All other flows fall through to the normal submit
    // confirmation.
    const body = buildBody();
    api.get("/admin/registry/certificates/duplicate-check", {
      params: {
        collector_name: body.collector_name,
        album_slug: body.album_slug,
        album_type: body.album_type,
        completion_date: body.completion_date,
      },
    })
      .then((r) => {
        if (r.data?.count > 0) {
          setDupWarning({ mode, body, matches: r.data.matches || [] });
        } else {
          setConfirming({ mode, body });
        }
      })
      .catch(() => {
        // Duplicate-check failure must not block the admin — fall
        // through to the normal submit-confirmation modal.
        setConfirming({ mode, body });
      });
  };

  const performSubmit = (mode, body) => {
    setSaving(true);
    setLastSaved(null);

    // Phase 4A · Client-generated Idempotency-Key. Same key on every
    // retry of THIS click → backend collapses replays. New key when the
    // admin edits fields and re-submits.
    const idemKey = isEdit
      ? undefined
      : (window.crypto?.randomUUID?.() || `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const cfg = idemKey ? { headers: { "Idempotency-Key": idemKey } } : undefined;

    const req = isEdit
      ? api.patch(`/admin/registry/certificates/${editing.cert_number}`, body)
      : api.post("/admin/registry/certificates", body, cfg);

    req
      .then((r) => {
        const savedCert = r.data.certificate;
        toast.success(isEdit ? "Certificate updated." : `${savedCert.cert_number} created.`);
        setLastSaved({ cert: savedCert, hint: r.data.next_sequence_hint || null });
        setConfirming(null);
        if (isEdit) {
          // Edit save always closes the modal — nothing new to show.
          onSaved(true);
        } else if (mode === "continue") {
          // Save-and-Add-Another: keep modal open, refresh list, clear fields
          // ready for the next entry. SuccessCard (with claim code) is visible
          // above the freshly-cleared form.
          setForm((f) => ({
            ...f,
            cert_number: "",
            collector_full_name: "",
            collector_nickname: "",
            public_display_name: "",
            completion_date: "",
            phone_number: "",
            instagram_handle: "",
            admin_notes: "",
            social_profile_permission: false,
            published: false,
            certificate_status: "verified",
          }));
          onSaved(false);
        } else {
          // PRIMARY create Save: keep modal open so the admin can read/copy
          // the newly-generated plaintext claim code from the SuccessCard
          // before dismissing. The admin closes explicitly via Cancel or the
          // X control at the top-right.
          onSaved(false);
        }
      })
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        setConfirming(null);
      })
      .finally(() => setSaving(false));
  };

  return (
    // Outer overlay — naturally-scrolling, TOP-anchored so tall modals never clip.
    // We deliberately avoid `flex items-center` here because when the modal's
    // content exceeds the viewport height, flex centering places the top edge
    // above the scroll origin and browsers cannot scroll to negative-position
    // content. `overflow-y-auto` on a block-flow container is the reliable
    // cross-browser pattern for scrollable modals of arbitrary height.
    <div
      className="fixed inset-0 z-50 bg-black/40 overflow-y-auto"
      data-testid="registry-admin-form-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="min-h-full flex justify-center py-8 px-4 pointer-events-none">
        <div
          className="pointer-events-auto bg-white rounded-2xl border-2 border-[var(--ink)] shadow-[8px_8px_0_var(--ink)] w-full max-w-2xl p-6 md:p-8"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="flex items-start justify-between">
          <div>
            <div className="kicker kicker-red">{isEdit ? "Edit certificate" : "New certificate"}</div>
            <h3 className="font-display text-2xl mt-1">
              Preview: <span className="font-mono text-[var(--panini-red)]" data-testid="registry-form-preview">{isEdit ? editing.cert_number : previewCertNumber}</span>
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--smoke)] hover:text-[var(--ink)]" data-testid="registry-form-close">
            <X size={20} />
          </button>
        </div>

        {lastSaved && !isEdit && (
          <SuccessCard
            saved={lastSaved.cert}
            onCreateAnother={() => {
              // Reset every collector-specific field. Preserves only
              // album_slug + album_type (harmless defaults per spec Part 4).
              setLastSaved(null);
              setFieldErrors({});
              setForm((f) => ({
                ...f,
                cert_number: "",
                collector_full_name: "",
                collector_nickname: "",
                public_display_name: "",
                completion_date: "",
                city: "",
                state: "",
                country: "",
                phone_number: "",
                instagram_handle: "",
                admin_notes: "",
                social_profile_permission: false,
                published: false,
                certificate_status: "verified",
              }));
            }}
          />
        )}

        {/* Edit-mode: show the existing claim code so the admin can copy or
            print it any time (matches the "visible in Admin" spec). */}
        {isEdit && editing?.claim_code && (
          <div className="mt-6 rounded-xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]/40 p-3" data-testid="registry-form-existing-claim-code-block">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">
              Claim Code
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-base text-[var(--panini-red)] tracking-widest" data-testid="registry-form-existing-claim-code-value">
                {editing.claim_code}
              </span>
              <button
                type="button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(editing.claim_code); toast.success("Claim code copied."); }
                  catch { toast.error("Copy failed"); }
                }}
                className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--ink)] px-3 py-1 text-xs font-bold hover:bg-[var(--ink)] hover:text-white transition-colors"
                data-testid="registry-form-existing-copy-claim-code"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>
        )}

        {/* Field order per spec §9:
            1 Full Name · 2 Nickname · 3 Public Display Name · 4 Album · 5 Cover Type
            · 6 Tournament Year (auto, read-only) · 7 Inspected On · 8 Public Notes
            · 9 Social Profile Permission · 10 Verified · 11 Published */}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Field label="Collector Full Name" required span2 error={fieldErrors.collector_full_name}>
            <input
              type="text"
              value={form.collector_full_name}
              onChange={(e) => {
                setForm({ ...form, collector_full_name: e.target.value });
                if (fieldErrors.collector_full_name) setFieldErrors({ ...fieldErrors, collector_full_name: null });
              }}
              className={`w-full rounded-lg border-2 px-3 py-2 ${fieldErrors.collector_full_name ? "border-[var(--panini-red)] bg-[var(--panini-red)]/5" : "border-[var(--ink)]"}`}
              data-testid="registry-form-collector-full-name"
            />
          </Field>
          <Field label="Collector Nickname" hint="Optional. Used on public certificate if display name is blank.">
            <input
              type="text"
              value={form.collector_nickname}
              onChange={(e) => setForm({ ...form, collector_nickname: e.target.value })}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2"
              data-testid="registry-form-collector-nickname"
            />
          </Field>
          <Field label="Public Display Name" hint="Shown on the certificate. Defaults to full name if empty.">
            <input
              type="text"
              value={form.public_display_name}
              onChange={(e) => setForm({ ...form, public_display_name: e.target.value })}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2"
              placeholder={form.collector_full_name || "e.g. Anderson V."}
              data-testid="registry-form-public-display-name"
            />
          </Field>
          <Field label="Album" required>
            <select
              value={form.album_slug}
              onChange={(e) => setForm({ ...form, album_slug: e.target.value })}
              disabled={isEdit}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2 bg-white"
              data-testid="registry-form-album-slug"
            >
              {ALBUMS.map((a) => (
                <option key={a.slug} value={a.slug}>{a.label}</option>
              ))}
            </select>
            {selectedAlbum && (
              <div className="mt-2 rounded-lg border border-[var(--ink)]/15 bg-[var(--panini-yellow-soft)]/40 p-2" data-testid="registry-form-album-preview">
                <AlbumCard slug={form.album_slug} size="sm" showTitle={true} coverType={form.album_type} testid="registry-form-album-preview-card" />
              </div>
            )}
          </Field>
          <Field label="Album Cover Type" required>
            <select
              value={form.album_type}
              onChange={(e) => setForm({ ...form, album_type: e.target.value })}
              disabled={isEdit}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2 bg-white"
              data-testid="registry-form-album-type"
            >
              <option value="HC">Hardcover</option>
              <option value="SC">Softcover</option>
            </select>
          </Field>
          <Field label="Tournament Year" hint="Automatically set from the album — read-only.">
            <input
              type="number"
              value={form.tournament_year}
              readOnly
              disabled
              className="w-full rounded-lg border-2 border-[var(--ink)]/30 bg-[var(--panini-yellow-soft)]/40 px-3 py-2 text-[var(--smoke)] font-mono"
              data-testid="registry-form-year"
            />
          </Field>
          <Field label="Inspected On (Completion Date)" required error={fieldErrors.completion_date}>
            <input
              type="date"
              value={form.completion_date}
              onChange={(e) => {
                setForm({ ...form, completion_date: e.target.value });
                if (fieldErrors.completion_date) setFieldErrors({ ...fieldErrors, completion_date: null });
              }}
              className={`w-full rounded-lg border-2 px-3 py-2 ${fieldErrors.completion_date ? "border-[var(--panini-red)] bg-[var(--panini-red)]/5" : "border-[var(--ink)]"}`}
              data-testid="registry-form-completion-date"
            />
          </Field>
          <Field
            label="Certificate Number"
            required={!isEdit}
            hint={
              isEdit
                ? "Immutable after save."
                : (seqHint
                    ? `Enter the exact number printed on the physical certificate. Suggested next: ${seqHint.preview_cert_number}`
                    : "Enter the exact number printed on the physical certificate (e.g. HC-2026-0015).")
            }
          >
            <input
              type="text"
              value={form.cert_number}
              onChange={(e) =>
                setForm({ ...form, cert_number: e.target.value.toUpperCase() })
              }
              placeholder={`${form.album_type}-${form.tournament_year}-0001`}
              disabled={isEdit}
              autoComplete="off"
              spellCheck={false}
              className={`w-full rounded-lg border-2 px-3 py-2 font-mono uppercase tracking-wider ${
                certNumberError
                  ? "border-[var(--panini-red)] bg-[var(--panini-red)]/5"
                  : "border-[var(--ink)]"
              }`}
              data-testid="registry-form-cert-number"
            />
            {certNumberError && !isEdit && (
              <div
                className="mt-1 text-xs font-semibold text-[var(--panini-red)]"
                data-testid="registry-form-cert-number-error"
              >
                {certNumberError}
              </div>
            )}
          </Field>
          <Field label="Phone Number (Private)" hint="Never shown publicly.">
            <input
              type="tel"
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2"
              data-testid="registry-form-phone"
            />
          </Field>
          <Field label="Instagram (Private)" hint="Never shown publicly.">
            <input
              type="text"
              value={form.instagram_handle}
              onChange={(e) => setForm({ ...form, instagram_handle: e.target.value })}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2"
              placeholder="@handle"
              data-testid="registry-form-instagram"
            />
          </Field>
          <Field label="Public Notes" span2 hint="Optional. Shown on the public certificate detail page.">
            <textarea
              rows={2}
              value={form.admin_notes}
              onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2"
              data-testid="registry-form-notes"
            />
          </Field>
          <Field label="Social Profile Permission" span2 hint="Collector consented to have their social handles surfaced on the public certificate page.">
            <label className="inline-flex items-center gap-2 mt-1">
              <input
                type="checkbox"
                checked={form.social_profile_permission}
                onChange={(e) => setForm({ ...form, social_profile_permission: e.target.checked })}
                className="w-5 h-5 accent-[var(--panini-red)]"
                data-testid="registry-form-social-permission"
              />
              <span className="text-sm">{form.social_profile_permission ? "Allowed" : "Not permitted"}</span>
            </label>
          </Field>
          <Field label="Verified Status" required>
            <select
              value={form.certificate_status}
              onChange={(e) => setForm({ ...form, certificate_status: e.target.value })}
              className="w-full rounded-lg border-2 border-[var(--ink)] px-3 py-2 bg-white"
              data-testid="registry-form-status"
            >
              <option value="verified">Verified</option>
              <option value="replacement">Replacement</option>
              <option value="revoked">Revoked</option>
              <option value="test">Test (never appears publicly)</option>
            </select>
          </Field>
          <Field label="Published Status">
            <label className="inline-flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="w-5 h-5 accent-[var(--panini-red)]"
                data-testid="registry-form-published"
              />
              <span className="text-sm">{form.published ? "Visible in public registry" : "Not published"}</span>
            </label>
          </Field>
        </div>

        <div className="mt-8 flex flex-col md:flex-row gap-3 justify-between">
          <div>
            {isEdit && (
              <DeleteButton
                cert={editing}
                onDeleted={() => { onSaved(true); }}
              />
            )}
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <button type="button" onClick={onClose} className="btn-ghost text-sm" data-testid="registry-form-cancel">Cancel</button>
            {!isEdit && (
              <button
                type="button" onClick={() => requestSubmit("continue")}
                disabled={saving}
                className={`btn-ghost text-sm ${!canSubmit ? "opacity-60" : ""}`}
              data-testid="registry-form-save-and-add"
            >
              {saving ? "Saving…" : "Save and Add Another"}
            </button>
          )}
          <button
            type="button" onClick={() => requestSubmit("close")}
            disabled={saving}
            className={`btn-red text-sm ${!canSubmit ? "opacity-70" : ""}`}
            data-testid="registry-form-save"
          >
            {saving ? "Saving…" : isEdit ? "Save Certificate" : "Save Certificate"}
          </button>
          </div>
        </div>
        </div>
      </div>

      {confirming && (
        <ConfirmSaveModal
          summary={{
            collectorName: collectorPrimaryName,
            fullName: form.collector_full_name,
            albumSlug: form.album_slug,
            albumLabel: selectedAlbum?.label || "",
            coverCode: form.album_type,
            coverLabel: coverDisplayLabel(form.album_type),
            year: form.tournament_year,
            certNumber: previewCertNumber,
            completionDate: form.completion_date,
          }}
          saving={saving}
          onCancel={() => setConfirming(null)}
          onConfirm={() => performSubmit(confirming.mode, confirming.body)}
        />
      )}

      {dupWarning && (
        <DuplicateWarningModal
          matches={dupWarning.matches}
          onCancel={() => setDupWarning(null)}
          onCreateAnyway={() => {
            const { mode, body } = dupWarning;
            setDupWarning(null);
            setConfirming({ mode, body });
          }}
        />
      )}
    </div>
  );
}

function DuplicateWarningModal({ matches, onCancel, onCreateAnyway }) {
  const [ack, setAck] = React.useState(false);
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
         data-testid="registry-form-dup-warning-modal">
      <div className="bg-white rounded-2xl border-2 border-[var(--panini-red)] shadow-[6px_6px_0_var(--ink)] w-full max-w-md p-6">
        <div className="kicker kicker-red">Possible duplicate</div>
        <h3 className="text-lg font-bold mt-1">This collector may already have a certificate</h3>
        <p className="mt-2 text-sm text-[var(--smoke)]">
          A certificate with the same collector name, album, cover type, and completion date already exists.
          Two collectors can share a name — but please double-check before creating another.
        </p>
        <div className="mt-4 space-y-2" data-testid="registry-form-dup-warning-matches">
          {matches.map((m) => (
            <div key={m.cert_number}
                 className="rounded-lg border-2 border-[var(--ink)] p-3 flex items-start justify-between gap-3">
              <div className="text-sm">
                <div className="font-mono font-bold text-[var(--panini-red)]" data-testid={`dup-match-cn-${m.cert_number}`}>
                  {m.cert_number}
                </div>
                <div>{m.collector_name} · {m.album_type} · {m.completion_date}</div>
                <div className="text-xs text-[var(--smoke)]">{m.is_claimed ? "Claimed" : "Unclaimed"}</div>
              </div>
              <a href={m.public_url} target="_blank" rel="noopener noreferrer"
                 className="text-xs font-bold underline underline-offset-2"
                 data-testid={`dup-match-view-${m.cert_number}`}>
                View Existing Certificate
              </a>
            </div>
          ))}
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                 data-testid="registry-form-dup-warning-ack" />
          I have reviewed the existing certificate and confirm this is a different collector.
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
                  className="rounded-full border-2 border-[var(--ink)] px-4 py-2 text-sm font-bold"
                  data-testid="registry-form-dup-warning-cancel">
            Cancel
          </button>
          <button type="button" onClick={onCreateAnyway} disabled={!ack}
                  className={`rounded-full border-2 border-[var(--panini-red)] bg-[var(--panini-red)] text-white px-4 py-2 text-sm font-bold ${!ack ? "opacity-50 cursor-not-allowed" : "hover:bg-[var(--panini-red-deep)]"}`}
                  data-testid="registry-form-dup-warning-create-anyway">
            Create Anyway
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmSaveModal({ summary, saving, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="registry-form-confirm-modal">
      <div className="bg-white rounded-2xl border-2 border-[var(--ink)] shadow-[6px_6px_0_var(--panini-red)] w-full max-w-md p-6">
        <div className="kicker kicker-red">Confirm certificate creation</div>
        <h4 className="font-display text-xl mt-1">Review before saving</h4>
        {summary.albumSlug && (
          <div className="mt-4 rounded-lg border border-[var(--ink)]/15 bg-[var(--panini-yellow-soft)]/40 p-3">
            <AlbumCard slug={summary.albumSlug} size="md" showTitle={true} coverType={summary.coverCode} testid="confirm-album-card" />
          </div>
        )}
        <dl className="mt-5 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--smoke)]">Collector</dt>
            <dd className="font-bold text-right" data-testid="confirm-collector">{summary.collectorName || summary.fullName || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--smoke)]">Album</dt>
            <dd className="font-bold text-right" data-testid="confirm-album">{summary.albumLabel || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--smoke)]">Cover Type</dt>
            <dd className="font-bold text-right" data-testid="confirm-cover">{summary.coverLabel || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--smoke)]">Tournament Year</dt>
            <dd className="font-bold text-right" data-testid="confirm-year">{summary.year || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--smoke)]">Certificate Number</dt>
            <dd className="font-mono text-[var(--panini-red)] text-right" data-testid="confirm-cert-number">{summary.certNumber}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-[var(--smoke)]">Inspected On</dt>
            <dd className="font-bold text-right" data-testid="confirm-date">{summary.completionDate || "—"}</dd>
          </div>
        </dl>
        <div className="mt-6 flex flex-col md:flex-row gap-3 justify-end">
          <button type="button" onClick={onCancel} className="btn-ghost text-sm" data-testid="confirm-cancel">Back to edit</button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="btn-red text-sm"
            data-testid="confirm-save"
          >
            {saving ? "Saving…" : "Confirm & create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SuccessCard({ saved, onCreateAnother }) {
  const publicUrl = `${window.location.origin}/certificates/${saved.cert_number}`;
  const firstName = (saved.collector_name || "").trim().split(/\s+/)[0] || "there";
  const deliveryMessage =
    `Congratulations ${firstName}! Your Album Complete Certificate is now live on CollectorUp.\n` +
    `View it here: ${publicUrl}\n` +
    `Your private claim code is: ${saved.claim_code || "«contact admin»"}\n` +
    `Sign in with Google, enter your code, and claim your CollectorUp profile.`;
  const copy = async (v, label) => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copied.`); } catch { toast.error("Copy failed"); }
  };
  return (
    <div className="mt-6 rounded-xl border-2 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)]/40 p-4" data-testid="registry-form-success">
      <div className="flex items-start gap-3">
        <CheckCircle2 size={20} className="text-[var(--panini-red)] mt-0.5" />
        <div className="flex-1">
          <div className="font-bold text-sm">Certificate Created Successfully</div>
          <div className="mt-1 text-sm text-[var(--ink)]" data-testid="registry-form-success-summary">
            <span className="font-mono">{saved.cert_number}</span> — {saved.collector_name}
            {saved.completion_date ? <span className="text-[var(--smoke)]"> · completed {saved.completion_date}</span> : null}
            {saved.album_type ? <span className="text-[var(--smoke)]"> · {saved.album_type}</span> : null}
            <span className="text-[var(--smoke)]"> · <span data-testid="registry-form-success-status">Unclaimed</span></span>
          </div>
          {saved.published ? (
            <div className="mt-1 text-xs text-[var(--smoke)]">Public URL: <span className="font-mono">{publicUrl}</span></div>
          ) : (
            <div className="mt-1 text-xs text-[var(--smoke)]">Draft (not yet published).</div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {saved.published && (
              <>
                <button type="button" onClick={() => copy(publicUrl, "Public link")}
                        className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--ink)] px-3 py-1 text-xs font-bold hover:bg-[var(--ink)] hover:text-white transition-colors"
                        data-testid="registry-form-copy-link">
                  <Copy size={12} /> Copy Certificate Link
                </button>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--ink)] px-3 py-1 text-xs font-bold hover:bg-[var(--ink)] hover:text-white transition-colors"
                   data-testid="registry-form-open-link">
                  <ExternalLink size={12} /> View Certificate
                </a>
              </>
            )}
            {saved.claim_code && (
              <button
                type="button"
                onClick={() => copy(deliveryMessage, "Delivery message")}
                className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--panini-red)] bg-[var(--panini-red)] text-white px-3 py-1 text-xs font-bold hover:bg-[var(--panini-red-deep)] transition-colors"
                data-testid="registry-form-copy-delivery-message"
                title="Personalised WhatsApp / email message with the URL + claim code baked in"
              >
                <Copy size={12} /> Copy Delivery Message
              </button>
            )}
            {onCreateAnother && (
              <button
                type="button"
                onClick={onCreateAnother}
                className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] px-3 py-1 text-xs font-bold hover:bg-[var(--ink)] hover:text-[var(--panini-yellow)] transition-colors"
                data-testid="registry-form-create-another"
              >
                Create Another Certificate
              </button>
            )}
            {saved.claim_code && (
              <div className="w-full mt-2 rounded-xl border-2 border-[var(--ink)] bg-white p-3" data-testid="registry-form-claim-code-block">
                <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">
                  Claim Code — admin-only
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-lg text-[var(--panini-red)] tracking-widest" data-testid="registry-form-claim-code-value">
                    {saved.claim_code}
                  </span>
                  <button
                    type="button"
                    onClick={() => copy(saved.claim_code, "Claim code")}
                    className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--ink)] px-3 py-1 text-xs font-bold hover:bg-[var(--ink)] hover:text-white transition-colors"
                    data-testid="registry-form-copy-claim-code"
                  >
                    <Copy size={12} /> Copy Claim Code
                  </button>
                </div>
                <div className="mt-1 text-[10px] text-[var(--smoke)]">
                  Give this to the collector — it never appears on any public surface.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteButton({ cert, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const doDelete = () => {
    if (typed.trim().toUpperCase() !== cert.cert_number.toUpperCase()) {
      toast.error("Confirmation text does not match the certificate number.");
      return;
    }
    setDeleting(true);
    api.delete(`/admin/registry/certificates/${cert.cert_number}`, {
      params: { confirm: cert.cert_number },
    })
      .then(() => {
        toast.success(`${cert.cert_number} deleted.`);
        onDeleted();
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"))
      .finally(() => setDeleting(false));
  };

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="inline-flex items-center gap-2 rounded-full border-2 border-red-500 text-red-600 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-500 hover:text-white transition-colors"
        data-testid="registry-form-delete"
      >
        <Trash2 size={14} /> Delete certificate
      </button>
    );
  }

  return (
    <div className="rounded-lg border-2 border-red-500 bg-red-50 p-4" data-testid="registry-form-delete-confirm">
      <div className="flex items-start gap-2 text-red-700">
        <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-bold">Delete {cert.cert_number}?</div>
          <p className="text-xs mt-1">
            This permanently removes the certificate and releases its number for reuse.
            Type <span className="font-mono font-bold">{cert.cert_number}</span> to confirm.
          </p>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={cert.cert_number}
            className="mt-3 w-full rounded border-2 border-red-500 px-3 py-1.5 text-sm font-mono bg-white"
            autoFocus
            data-testid="registry-form-delete-input"
          />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={doDelete}
              disabled={deleting || typed.trim().toUpperCase() !== cert.cert_number.toUpperCase()}
              className="rounded-full bg-red-600 text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="registry-form-delete-submit"
            >
              {deleting ? "Deleting…" : "Confirm delete"}
            </button>
            <button
              type="button"
              onClick={() => { setConfirming(false); setTyped(""); }}
              className="rounded-full border-2 border-[var(--ink)] px-4 py-1.5 text-xs font-bold uppercase tracking-widest"
              data-testid="registry-form-delete-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, span2, error, children }) {
  return (
    <div className={span2 ? "md:col-span-2" : undefined}>
      <label className="block text-xs uppercase tracking-widest text-[var(--smoke)] mb-1">
        {label}{required && <span className="text-[var(--panini-red)] ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs font-semibold text-[var(--panini-red)] mt-1" data-testid="registry-form-field-error">
          {error}
        </p>
      )}
      {!error && hint && <p className="text-[10px] text-[var(--smoke)] mt-1">{hint}</p>}
    </div>
  );
}

function Pill({ on, tone, children }) {
  const cls = on
    ? tone === "gold"
      ? "bg-[var(--gm-gold)]/20 text-[var(--gm-gold)] border-[var(--gm-gold)]/40"
      : "bg-[var(--panini-red)]/10 text-[var(--panini-red)] border-[var(--panini-red)]/30"
    : "bg-black/5 text-[var(--smoke)] border-black/10";
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold border ${cls}`}>{children}</span>;
}

function StatusPill({ status }) {
  const map = {
    verified:    { c: "bg-green-100 text-green-700 border-green-200",     l: "Verified" },
    replacement: { c: "bg-blue-100 text-blue-700 border-blue-200",       l: "Replacement" },
    revoked:     { c: "bg-red-100 text-red-700 border-red-200",          l: "Revoked" },
    test:        { c: "bg-gray-100 text-gray-700 border-gray-200",       l: "Test" },
  };
  const info = map[status] || map.verified;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold border ${info.c}`}>{info.l}</span>;
}
